import React from "react";
import "../App.css";

const sections = [
	{
		title: "1. Introduction and Scope",
		body: [
			`KorvRFM ("we", "our", or "the Platform") is a B2B customer intelligence and RFM (Recency, Frequency, Monetary) analytics service operated by Clustrex Technologies. This Privacy Policy explains how we collect, process, store, use, disclose, and protect personal data in connection with our platform, including data processed through the Meta WhatsApp Business API.`,
			`This Policy applies to business clients who integrate KorvRFM into their operations ("Operators"), end-users whose data Operators submit to or process through the Platform ("Data Subjects"), and visitors to our web interfaces. By using KorvRFM or the WhatsApp integration, Operators confirm their acceptance of this Policy and agree to obtain all necessary consents from Data Subjects.`,
			`KorvRFM uses the WhatsApp Business API. WhatsApp-related data processing is also subject to Meta's Platform Terms, WhatsApp Business API Terms, WhatsApp Business Messaging Policy, and applicable data processing agreements. Operators must separately comply with Meta's messaging requirements and local telecommunications regulations.`,
		],
	},
	{
		title: "2. Data We Collect",
		body: [
			`Operators may provide customer transaction records, purchase dates, purchase frequency, product categories, customer names, email addresses, phone numbers, RFM scores, lifecycle labels, churn-related labels, business configuration data, segment rules, and CRM import files such as CSV or XLSX files.`,
			`When Operators enable WhatsApp, the Platform may process WhatsApp phone numbers, WhatsApp Business Account identifiers, phone number IDs, message delivery status, template response data, opt-in and opt-out signals, and related message metadata strictly for the Operator's business use case.`,
			`We may also process technical data such as IP addresses, user agents, API request logs, authentication/session identifiers, and dashboard usage events for security, troubleshooting, fraud prevention, and platform operation.`,
		],
	},
	{
		title: "3. Legal Basis for Processing",
		body: [
			`We process personal data where necessary to provide the KorvRFM service under an Operator's subscription agreement, to protect legitimate platform security interests, to comply with legal obligations, and to act on documented Operator instructions.`,
			`WhatsApp message delivery requires valid opt-in consent from the end-user. Operators are responsible for obtaining, recording, and maintaining that consent before sending WhatsApp messages through KorvRFM.`,
		],
	},
	{
		title: "4. WhatsApp-Specific Data Handling",
		body: [
			`Operators must obtain and document explicit opt-in consent from each end-user before sending WhatsApp messages through KorvRFM. Opt-in records should identify the categories of messages the user agreed to receive, such as transactional, promotional, or engagement messages.`,
			`Operators must provide an accessible opt-out mechanism, such as "Reply STOP to unsubscribe." Opt-out signals are processed and suppression lists are honored so users who opt out do not receive further messages through the Platform.`,
			`KorvRFM does not use WhatsApp data to build cross-platform advertising profiles, does not sell WhatsApp conversation data, and does not share WhatsApp data with third parties for commercial resale.`,
			`All WhatsApp message templates used through KorvRFM must follow Meta requirements and must be approved by Meta where approval is required. Operators are responsible for ensuring their templates and campaigns comply with Meta policies and applicable law.`,
		],
	},
	{
		title: "5. Prohibited Uses",
		body: [
			`Operators may not use KorvRFM to send messages to users who have not opted in, send spam or unsolicited bulk messages, transmit content that violates Meta policies, collect sensitive credentials or government identifiers over WhatsApp, bypass Meta's template approval process, or re-identify anonymized or pseudonymized data.`,
		],
	},
	{
		title: "6. How We Use Data",
		body: [
			`We use data to compute RFM scores, create customer segments, support predictive analytics, deliver WhatsApp campaign triggers, schedule messages, track delivery status, authenticate requests, maintain platform security, provide support, generate aggregated analytics, comply with legal obligations, and detect fraud or abuse.`,
			`We do not sell, rent, license, or otherwise monetize personal data to third parties. We do not use personal data for behavioral advertising targeting.`,
		],
	},
	{
		title: "7. Data Sharing and Third-Party Disclosures",
		body: [
			`Meta may receive phone numbers, message content, templates, and delivery metadata as necessary to deliver WhatsApp messages. Meta processes this data according to its own applicable terms and policies.`,
			`KorvRFM may use cloud infrastructure providers such as Amazon Web Services for compute, storage, database, and operational services. We may also use self-hosted authentication services such as Keycloak for identity and session management.`,
			`We may disclose data to law enforcement, regulators, or courts where required by applicable law, court order, or governmental authority. Where legally permitted, we will notify the relevant Operator.`,
		],
	},
	{
		title: "8. International Data Transfers",
		body: [
			`KorvRFM is primarily hosted on AWS infrastructure in the ap-south-1 (Mumbai) region for Indian deployments. If personal data is transferred outside India, the European Economic Area, or another protected jurisdiction, we use appropriate contractual, technical, and organizational safeguards as required by applicable law.`,
		],
	},
	{
		title: "9. Data Retention",
		body: [
			`Transaction and RFM data is retained for the duration of the Operator's active subscription and for a limited post-termination export period unless a different agreement applies. WhatsApp delivery logs and API access logs are retained only as long as needed for service operation, support, compliance, security, and dispute resolution.`,
			`Opt-in and opt-out records may be retained for compliance evidence. Billing and contract records may be retained as required by tax, accounting, and legal obligations. Aggregated analytics that no longer identify individuals may be retained indefinitely.`,
		],
	},
	{
		title: "10. Security Measures",
		body: [
			`KorvRFM uses technical and organizational safeguards including TLS for data in transit, encryption at rest where supported, role-based access controls, least-privilege access, audit logging, network isolation, API authentication, vulnerability management, and incident response procedures.`,
			`Operators are responsible for securing their own credentials, webhook endpoints, user accounts, and any data stored outside KorvRFM.`,
		],
	},
	{
		title: "11. Data Subject Rights",
		body: [
			`Depending on applicable law, Data Subjects may have rights to access, correct, delete, restrict, port, object to processing of, or withdraw consent for their personal data. Requests from Data Subjects should generally be submitted to the relevant Operator, because KorvRFM processes most business customer data on behalf of Operators.`,
			`When an Operator forwards a verified request to us, we will assist the Operator in responding within the timeframe required by applicable law.`,
		],
	},
	{
		title: "12. Children's Privacy",
		body: [
			`KorvRFM is a B2B platform intended for business use. We do not knowingly collect, process, or store personal data of individuals under 18 years of age. Operators must not submit minors' data to the Platform unless they have a lawful basis and a written agreement covering such processing.`,
		],
	},
	{
		title: "13. Cookies and Tracking",
		body: [
			`The KorvRFM dashboard may use strictly necessary cookies, authentication tokens, browser storage, CSRF protections, and functional preferences. We do not use advertising cookies, cross-site tracking pixels, or third-party marketing tags in the Platform dashboard.`,
		],
	},
	{
		title: "14. Operator Obligations",
		body: [
			`Operators must obtain lawful consent and permissions before uploading customer data or sending WhatsApp messages, maintain their own customer-facing privacy notice, immediately notify KorvRFM of suspected security incidents involving Platform data, ensure submitted data is accurate and lawfully collected, and comply with applicable privacy, telecom, and messaging laws.`,
			`Operators must not use KorvRFM to process sensitive personal data such as health, biometric, financial credential, religious, political, or government identifier data unless separately agreed in writing.`,
		],
	},
	{
		title: "15. Changes to This Policy",
		body: [
			`We may update this Privacy Policy to reflect changes in our practices, technology, legal requirements, or operational needs. Material changes will be communicated to Operators by email, in-platform notice, or other reasonable means before or when the changes take effect.`,
		],
	},
	{
		title: "16. Contact and Grievance Redressal",
		body: [
			`For privacy inquiries, data subject requests, or concerns, contact Clustrex Data Private Limited at connect@clustrex.com.`,
			`Grievance Officer: venkat@clustrex.com. Mailing address: Clustrex Data Private Limited, No. 51/2 - II Floor, Pandian Complex, Madipakkam Main Rd, Puzhuthivakkam, Madipakkam, Chennai, Tamil Nadu 600091, India.`,
		],
	},
];

export default function PrivacyPolicy() {
	return (
		<main className="legal-page">
			<section className="legal-hero">
				<img src="/rfm-logo.png" alt="KorvRFM" className="legal-logo" />
				<div>
					<p className="legal-kicker">KorvRFM - Customer Intelligence Platform</p>
					<h1>Privacy Policy</h1>
					<p>
						Including WhatsApp Business API (Meta) integration. Effective Date: May 26, 2025.
						Version 1.0.
					</p>
				</div>
			</section>

			<section className="legal-card">
				{sections.map((section) => (
					<article className="legal-section" key={section.title}>
						<h2>{section.title}</h2>
						{section.body.map((paragraph) => (
							<p key={paragraph}>{paragraph}</p>
						))}
					</article>
				))}
			</section>
		</main>
	);
}
